export const meta = {
  id: '18-pronouns',
  order: 18,
  titleFi: 'Pronominit',
  titleEn: 'Pronouns',
  duolingoUnit: 'duo-20-pronouns',
  lemmaIds: ['nyke', 'ao', 'īlon', 'jeme', 'pōnta', 'jorrāelagon', 'ñuhon', 'aōhon', 'zȳhon', 'jāhon', 'īlvon', 'jevon', 'pōjon', 'ours:dēstir', 'ours:rakerys', 'ours:majēdegon', 'ours:botennio', 'ours:gīmirys', 'ours:tyntys', 'ours:dēzāzma', 'ours:arotāpnon', 'ours:suomīha', 'ours:suomīhy', 'ours:nerdi', 'ours:lesbiani'],
  exampleIds: ['ex-c53f25e7a310', 'ex-c9d2a2dffa65', 'hotd-s2e08-003', 'got-s8e06-005', 'ex-cd113b1637ad', 'ex-81472ac21a7d', 'ex-a4c88269a589', 'ex-f3dc917d299d', 'ex-250ed447f985'],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Personal_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Possessive_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=Jorrāelagon',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['personal'],
  grammarAlso: ['possessives'],
  showLines: ['hotd-s2e08-003', 'got-s8e06-005'],
  pronunciation: { focus: 'z', words: ['zȳhon', 'hōzikagon', 'zāliapos', 'zokla', 'dubāzma'], lines: ['got-s8e06-005'] },
  letter: {
    fi: 'Kerro meistä: minä, sinä, me ja he.',
    en: 'Write about us: I, you, we and they.',
  },
  scene: 'hotd-s2e08-1',
}

# Pronominit

Minä, sinä, hän, me, te, he – ja minun, sinun, hänen. Persoonapronominit ovat kielen pienimpiä ja käytetyimpiä sanoja, ja juuri siksi ne taipuvat usein omalla, arvaamattomalla tavallaan. Tällä oppitunnilla opit korkeavalyrian seitsemän persoonapronominia ja niiden taivutuksen tähän asti opituissa sijoissa. Lisäksi palataan omistuspronomineihin, jotka tulivat vastaan jo <LessonLink n={11}>oppitunnilla 11</LessonLink>: *minun omani, sinun omasi*.

Aloitetaan lauseella, joka on teille kahdelle ehkä kaikkein tärkein:

<Example id="ex-c53f25e7a310" />

<Hv glossed>Avy</Hv> on sinä-pronominin akkusatiivi: ’sinua’. Kaksi verbiä, kaksi tapaa rakastaa: <Hv glossed>jorrāelagon</Hv> ’pitää rakkaana, vaalia’ ja <Hv glossed>raqagon</Hv> ’pitää jostakin, rakastaa’. Tämän oppitunnin sana <Hv glossed>jorrāelagon</Hv> tarkoittaa lisäksi ’tarvita, vaatia’. Asiayhteys ratkaisee, kumpi merkitys on kyseessä.

## Seitsemän persoonapronominia

Nominatiivissa pronominit ovat:

- <Hv glossed>nyke</Hv> ’minä’
- <Hv glossed>ao</Hv> ’sinä’
- <Hv>ziry</Hv> tai <Hv glossed>ūja</Hv> ’hän, se’
- <Hv glossed>īlon</Hv> ’me’
- <Hv glossed>jeme</Hv> ’te’
- <Hv glossed>pōnta</Hv> ’he, ne’

Seitsemän siksi, että yksikön 3. persoonassa on kaksi pronominia. Niistä kohta lisää.

**Nominatiivia käytetään harvoin.** Verbin persoonapääte kertoo jo, kuka tekee: <Hv glossed>jorrāelan</Hv> on ’rakastan’ ilman pronominiakin. Pronomini lisätään vain, kun sitä halutaan korostaa. Suomalaiselle tämä on tuttua: sanomme *rakastan sinua* emmekä *minä rakastan sinua*, ellei *minä* ole painollinen. Ero on siinä, että suomen kirjakielessä 3. persoona tarvitsee pronominin (*hän rakastaa*), valyriassa taas pronominia ei tarvita missään persoonassa.

Korostusta tarvitaan esimerkiksi silloin, kun kaksi ihmistä asetetaan rinnakkain. Rhaenyran lauseen näit jo <LessonLink n={3}>oppitunnilla 3</LessonLink>: hän sanoo ’sinä ja minä’, <Hv glossed>ao nykē</Hv>. Sanan <Hv glossed>nyke</Hv> loppuvokaali on pidentynyt, ja pidennys tarkoittaa tässä ’ja’, kuten <LessonLink n={2}>oppitunnilla 2</LessonLink> opit. Verbi <Hv glossed>iksi</Hv> ’olemme’ riittäisi yksinäänkin, mutta pronominit tekevät lauseesta henkilökohtaisen:

<Example id="ex-c9d2a2dffa65" />

## Ziry vai ūja?

Korkeavalyriassa on neljä sukua, mutta pronomineissa ne eivät kaikki näy. Yksikön 3. persoonassa on kaksi pronominia, ja valinta tehdään **sen substantiivin suvun mukaan, johon pronomini viittaa**:

- kuu- tai aurinkosukuinen tarkoite → <Hv>ziry</Hv>
- maa- tai vesisukuinen tarkoite → <Hv>ūja</Hv>

Jos tarkoite on epäselvä, elävistä olennoista käytetään tavallisesti pronominia <Hv>ziry</Hv> ja elottomista asioista pronominia <Hv>ūja</Hv>. Elävät olennot ovat nimittäin yleensä kuu- tai aurinkosukua, ja maa- ja vesisuvun sanat tarkoittavat yleensä elottomia asioita.

Ensi näkemältä suomesta tuntuu olevan tässä apua: meidänkin kirjakielessämme on kaksi pronominia, *hän* ihmisistä ja *se* muista. Suomessa raja kulkee kuitenkin ihmisen ja muun välillä, valyriassa **suvun** mukaan. Duolingon Tips & Notesin esimerkki näyttää eron täydellisesti. <Hv glossed>Mandia</Hv> ’isosisko’ on kuusukua, <Hv glossed>hāedar</Hv> ’pikkusisko’ vesisukua. Kun sanot ’rakastan häntä’, pronomini riippuu siitä, kumpaa siskoa tarkoitat:

- <Hv glossed>Ziry jorrāelan.</Hv> ’Rakastan häntä (isosiskoa).’
- <Hv glossed>Ūī jorrāelan.</Hv> ’Rakastan häntä (pikkusiskoa).’

<Interlinear hv="Ūī jorrāel|an." gloss="hän-AQ.ACC rakastaa-PRS.1SG" fi="Rakastan häntä (pikkusiskoa)." />

Kumpikin on ihminen, ja suomeksi kumpikin on *hän*. Valyriassa pikkusisko on <Hv>ūja</Hv>-pronominin akkusatiivi <Hv>ūī</Hv>, aivan kuin hän olisi asia. Mitään vähättelevää siinä ei ole – se on pelkkää kielioppia.

Wikin kielioppisivu lisää, että sama koskee nimiä: jos ihmisen nimi on maasukuinen, häneen viitataan maasuvun mukaan. Esimerkkinä on <Hv>Daemon</Hv>. Kun tarkempaa tietoa ei ole, ihmisiin viitataan oletuksena kuusuvun mukaan.

Daemonin repliikissä <Hv>ūī</Hv> viittaa asiaan eikä ihmiseen – englanninkielisessä käännöksessä se on *it*:

<Line id="hotd-s2e08-003" />

Daenerysin käskyssä taas <Hv>ziry</Hv> viittaa ihmiseen:

<Line id="got-s8e06-005" />

## Pronominien taivutus

Duolingon ohjeiden sanoin muut pronominit kuin <Hv>pōnta</Hv> taipuvat arvaamattomasti. <Hv>Pōnta</Hv> taipuu kuin tavallinen <Hv>a</Hv>-loppuinen kuusukuinen substantiivi (<Hv>pōnte</Hv>, <Hv>pōnto</Hv>, <Hv>pōntot</Hv> – vertaa <Hv>vale</Hv>, <Hv>valo</Hv>, <Hv>valot</Hv>). Muut on opeteltava sellaisinaan. Taulukoissa ovat tähän mennessä opitut sijat; loput tulevat vastaan myöhemmin.

<Paradigm lemma="nyke" filter="1.sg" cases="nom,acc,gen,dat" />

<Paradigm lemma="ao" filter="2.sg" cases="nom,acc,gen,dat" />

<Paradigm lemma="ziry" filter="lunar" cases="nom,acc,gen,dat" />

<Paradigm lemma="ūja" filter="terrestrial" cases="nom,acc,gen,dat" />

Näissä kahdessa taulukossa on vain yksi suku kummastakin parista: <Hv>ziry</Hv>-pronominin aurinkosuvun muodot ovat samat kuin kuusuvun, ja <Hv>ūja</Hv>-pronominin vesisuvun muodot samat kuin maasuvun.

<Paradigm lemma="īlon" filter="1.pl" cases="nom,acc,gen,dat" />

<Paradigm lemma="jeme" filter="2.pl" cases="nom,acc,gen,dat" />

<Paradigm lemma="pōnta" filter="3.pl" cases="nom,acc,gen,dat" />

Muutama muistisääntö. Minä-pronominin akkusatiivi, genetiivi ja datiivi alkavat samalla tavalla: <Hv glossed>yne</Hv> ’minua’, <Hv glossed>yno</Hv> ’minun’, <Hv glossed>ynot</Hv> ’minulle’. Datiivit päättyvät tuttuun <Hv>t</Hv>:hen (<Hv l="nyke">ynot</Hv>, <Hv>aōt</Hv>, <Hv>zijot</Hv>, <Hv>ūjot</Hv>, <Hv>īlot</Hv>, <Hv l="jeme">jemot</Hv>, <Hv>pōntot</Hv>), joten datiivin oppitunnista on apua niiden tunnistamisessa. Lisäksi <Hv>ziry</Hv> on samanlainen nominatiivissa ja akkusatiivissa.

<Pronounce word="jorrāelagon" />

## Omistuspronominit: minun omani

Omistusadjektiivien oppitunnilla opit sanoja kuten <Hv glossed>ñuha</Hv> ’minun’, jotka kuvaavat substantiivia: *minun kirjani*. Omistuspronomini on eri asia: se **korvaa** koko omistusta ilmaisevan sanaryhmän. Vertaa:

- *Se on hänen kirjansa.* – omistusadjektiivi
- *Se kirja on hänen.* – omistuspronomini

Suomesta ei ole tässä mitään apua, sillä meillä kumpikin on sama genetiivi *hänen*. Valyriassa ne ovat eri sanoja.

<Compare l="ñuha" fi="minu|n" hv="ñuh|a" label="omistusadjektiivi: minun (kirjani)" />

<Compare l="ñuhon" fi="minu|n" hv="ñuh|on" label="omistuspronomini: (kirja on) minun" />

Omistuspronominit ovat muodoltaan **maasukuisia, <Hv>-on</Hv>-loppuisia substantiiveja**:

- <Hv glossed>ñuhon</Hv> ’minun (omani)’
- <Hv glossed>aōhon</Hv> ’sinun (omasi)’
- <Hv>zȳhon</Hv> tai <Hv glossed>jāhon</Hv> ’hänen (omansa)’
- <Hv glossed>īlvon</Hv> ’meidän (omamme)’
- <Hv glossed>jevon</Hv> ’teidän (omanne)’
- <Hv glossed>pōjon</Hv> ’heidän (omansa)’

<Hv>Zȳhon</Hv> tai <Hv l="jāhon">jāhon</Hv> valitaan samalla periaatteella kuin <Hv>ziry</Hv> tai <Hv>ūja</Hv>: omistajan suvun mukaan. Koska ne ovat substantiiveja, ne taipuvat kuten mikä tahansa kolmannen <Term id="deklinaatio">taivutusluokan</Term> maasukuinen sana:

<Paradigm lemma="ñuhon" cases="nom,acc,gen,dat" numbers="sg,pl" />

Tässä esimerkissä on koko oppitunti pienoiskoossa. Ensimmäisessä lauseessa on painokas <Hv>ao</Hv> ja omistusadjektiivi <Hv l="aōha">aōhor</Hv>, joka on vesisuvun muodossa, koska <Hv>hāedar</Hv> on vesisukua. Toisessa lauseessa omistaja on <Hv>Targārien</Hv>, joka on maasukuinen nimi, ja siksi pronomini on <Hv l="jāhon">jāhon</Hv> eikä <Hv>zȳhon</Hv>. Kielteisen lauseen verbi on lisäksi subjunktiivissa:

<Example id="ex-cd113b1637ad" />

Omistuspronomini taipuu myös luvussa. Ensimmäisessä esimerkissä omistettuja on monta, joten muodot <Hv fi="hänen omansa">zȳha</Hv> ja <Hv l="pōjon" fi="heidän omansa">pōja</Hv> ovat monikossa. Toisessa <Hv l="pōjon">pōjon</Hv> on yksikössä:

<Example id="ex-a4c88269a589" />

<Example id="ex-f3dc917d299d" />

<Note>
Yksi kohta vaatii tarkkuutta: omistusadjektiivin maasuvun yksikön nominatiivi ja akkusatiivi näyttävät samalta kuin omistuspronomini. Kun <Hv l="ñuha">ñuha</Hv> kuvaa maasukuista sanaa, sen muoto on <Hv glossed>ñuhon</Hv> – siis ’minun’ tietyn sanan yhteydessä. Duolingon ohjeet korostavat, että valyria erottaa nämä kaksi sanaluokkaa aina, mutta lauseessa eron ratkaisee rakenne eikä pelkkä muoto: omistuspronomini on yksin, ilman substantiivia, jota se kuvaisi. Tässä esimerkissä <Hv>zȳhon</Hv> on adjektiivi, koska se kuvaa maasukuista sanaa <Hv glossed>grevion</Hv> ’vyötärö’:

<Example id="ex-250ed447f985" />
</Note>

Ja vielä lause teille kahdelle:

<Interlinear hv="Aōh|on iksan se ñuh|on iksā." gloss="sinun.omasi-NOM olla-PRS.1SG ja minun.omani-NOM olla-PRS.2SG" fi="Olen sinun, ja sinä olet minun." />

<Example id="ex-81472ac21a7d" />

<Pronounce l="aōhon" word="aōhon" />

## Harjoituslauseita

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukoiden muodoilla. Ne eivät ole Petersonin lauseita.

<Practice hv="Nyke avy jorrāelan." fi="Minä rakastan sinua." />

<Practice hv="Pōnta ynot havon tepis." fi="He antavat minulle leipää." />

<Practice hv="Kēli ñuhon issa." fi="Kissa on minun." />

<Practice hv="Ūī nevion daor." fi="En kanna sitä." />

Ensimmäisessä lauseessa <Hv>nyke</Hv> on mukana vain korostuksen vuoksi. Viimeisessä <Hv>ūī</Hv> voisi viitata vaikkapa leipään, <Hv>havon</Hv>: maasukuiseen sanaan viitataan pronominilla <Hv>ūja</Hv>.

<Note>
**Sama lause kuudella pronominilla.** Damien Halme (Academy of High Valyrian) harjoittaa pronomineja ja verbin päätteitä yhdessä: sano sama lause kaikilla persoonilla rytmikkäästi peräkkäin.

<Practice hv="Nyke rōbra jaelan." fi="Minä haluan viikunoita." />

<Practice hv="Ao rōbra jaelā." fi="Sinä haluat viikunoita." />

<Practice hv="Ziry rōbra jaelza." fi="Hän haluaa viikunoita." />

<Practice hv="Īlon rōbra jaeli." fi="Me haluamme viikunoita." />

<Practice hv="Jeme rōbra jaelāt." fi="Te haluatte viikunoita." />

<Practice hv="Pōnta rōbra jaelzi." fi="He haluavat viikunoita." />
</Note>

## Kuka teki kenelle?

Valitse, kumpi suomennos on oikea. Sen kertovat päätteet, ei sanajärjestys.

<KukaTeki />

## Sanat

<Vocab />

<Check />
